// © 2026 AttainIP Engineering
//
// TooltipHost — ONE global tooltip controller. Mount it once at the app root.
// It event-delegates hover/focus on any `[data-tooltip]` element (works for
// React markup AND generated HTML, e.g. citation jump-links) and renders a
// single reused, portaled pill that is ALWAYS kept within the viewport
// (positionTooltip) and wraps at a comfortable measure (tooltipPill).
//
// This supersedes the old CSS `.attn-tooltip::after`, which — being pure CSS —
// could not clamp to the viewport and overflowed near screen edges. Elements
// only need `data-tooltip="…"`; no class and no per-element positioning are
// required.

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { positionTooltip } from './tooltipPositioning';
import { tooltipPillStyle, TOOLTIP_DELAY_INSTANT, TOOLTIP_DELAY_HINT } from './tooltipPill';
import type { TooltipPlacement } from './tooltipPositioning';

/** Resolve an element's show delay: source/disclosure tooltips are near-instant
 *  by default; those marked `data-tooltip-delay="hint"` (or a raw ms number)
 *  wait a beat. */
function resolveDelay(el: HTMLElement): number {
  const raw = el.getAttribute('data-tooltip-delay');
  if (raw === 'hint') return TOOLTIP_DELAY_HINT;
  if (raw && !Number.isNaN(Number(raw))) return Number(raw);
  return TOOLTIP_DELAY_INSTANT;
}

interface ActiveTip {
  el: HTMLElement;
  text: string;
}

/**
 * Has the pointer moved far enough since a click to lift the tooltip
 * suppression that click imposed?
 *
 * Exported and pure because the behaviour it decides cannot be reached by a
 * jsdom test: the floating tip is positioned from measurements jsdom does not
 * produce, so a component test of it passes whether or not the logic is
 * right. The threshold exists because a click alone can emit a mousemove of a
 * pixel or two, which would lift the suppression before the menu it opened
 * had even painted.
 */
export const TOOLTIP_RESUME_MOVE_PX = 4;

export function pointerMovedEnough(
  from: { x: number; y: number } | null, x: number, y: number,
): boolean {
  if (!from) return false;
  return Math.abs(x - from.x) + Math.abs(y - from.y) > TOOLTIP_RESUME_MOVE_PX;
}

/** Nearest ancestor (incl. self) carrying a non-empty `data-tooltip`. */
function tooltipTarget(node: EventTarget | null): HTMLElement | null {
  if (!(node instanceof Element)) return null;
  const el = node.closest<HTMLElement>('[data-tooltip]');
  const text = el?.getAttribute('data-tooltip');
  return el && text && text.trim() ? el : null;
}

export function TooltipHost() {
  const [active, setActive] = useState<ActiveTip | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number; placement: TooltipPlacement } | null>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const showTimer = useRef<number | null>(null);
  /**
   * A click suppresses tooltips until the pointer actually MOVES again.
   *
   * Dismissing on pointerdown alone was not enough. Clicking a control that
   * opens something — a menu, a popover — puts NEW elements under a cursor
   * that has not moved, and the `mouseover` those generate re-showed the tip
   * on top of the very menu the click had just opened. A tooltip is a hint
   * about a thing you have not acted on yet; once you act, it should stay
   * gone until you go looking again.
   */
  const suppressedAt = useRef<{ x: number; y: number } | null>(null);

  // ── Hover / focus delegation ──────────────────────────────────────────────
  useEffect(() => {
    const clearTimer = () => {
      if (showTimer.current) {
        window.clearTimeout(showTimer.current);
        showTimer.current = null;
      }
    };
    const scheduleShow = (el: HTMLElement) => {
      if (suppressedAt.current) return;
      clearTimer();
      showTimer.current = window.setTimeout(() => {
        setPos(null); // re-measure before the next paint
        setActive({
          el,
          text: el.getAttribute('data-tooltip') ?? '',
        });
      }, resolveDelay(el));
    };
    const dismiss = (el: HTMLElement) => {
      clearTimer();
      setActive((cur) => (cur && cur.el === el ? null : cur));
    };

    const onOver = (e: MouseEvent) => {
      const el = tooltipTarget(e.target);
      if (el) scheduleShow(el);
    };
    const onOut = (e: MouseEvent) => {
      const el = tooltipTarget(e.target);
      if (!el) return;
      const to = e.relatedTarget as Node | null;
      if (to && el.contains(to)) return; // moving within the same target
      dismiss(el);
    };
    const onFocusIn = (e: FocusEvent) => {
      const el = tooltipTarget(e.target);
      if (!el) return;
      // Tabbing to a control IS going looking, and a keyboard user may never
      // move the pointer to lift the suppression a click left behind.
      suppressedAt.current = null;
      scheduleShow(el);
    };
    const onFocusOut = (e: FocusEvent) => {
      const el = tooltipTarget(e.target);
      if (el) dismiss(el);
    };

    document.addEventListener('mouseover', onOver, true);
    document.addEventListener('mouseout', onOut, true);
    document.addEventListener('focusin', onFocusIn, true);
    document.addEventListener('focusout', onFocusOut, true);
    return () => {
      clearTimer();
      document.removeEventListener('mouseover', onOver, true);
      document.removeEventListener('mouseout', onOut, true);
      document.removeEventListener('focusin', onFocusIn, true);
      document.removeEventListener('focusout', onFocusOut, true);
    };
  }, []);

  // ── Measure + position (viewport-clamped), and follow scroll/resize ───────
  useLayoutEffect(() => {
    if (!active) return;
    const place = () => {
      const el = active.el;
      const tip = tipRef.current;
      if (!tip) return;
      if (!el.isConnected) {
        setActive(null);
        return;
      }
      const r = el.getBoundingClientRect();
      const t = tip.getBoundingClientRect();
      // `data-tooltip-placement` lets a trigger ask for a side. Margin-rail
      // controls ask for "right": above/below would cover the paragraph the
      // control acts on, which is the one thing a tooltip must not hide.
      const asked = el.getAttribute('data-tooltip-placement');
      const preferred: TooltipPlacement =
        asked === 'right' || asked === 'left' || asked === 'bottom' || asked === 'top'
          ? asked
          : 'top';
      const p = positionTooltip(
        { top: r.top, left: r.left, width: r.width, height: r.height },
        { width: t.width, height: t.height },
        { width: window.innerWidth, height: window.innerHeight },
        preferred,
      );
      setPos({ top: p.top, left: p.left, placement: p.placement });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [active]);

  // Hover then click (opening a menu / stealing focus) should dismiss the tip.
  useEffect(() => {
    if (!active) return;
    const onDown = (event: PointerEvent) => {
      suppressedAt.current = { x: event.clientX, y: event.clientY };
      setActive(null);
      // Watch for movement ONLY while a click is actually suppressing, and
      // stop the moment it lifts. A permanently mounted capture-phase
      // mousemove listener is the most expensive thing this component could
      // own — it runs on every pointer movement in the app, for the whole
      // session, to serve a state that exists for a second after a click.
      const onMove = (e: MouseEvent) => {
        if (pointerMovedEnough(suppressedAt.current, e.clientX, e.clientY)) {
          suppressedAt.current = null;
          document.removeEventListener('mousemove', onMove, true);
        }
      };
      document.addEventListener('mousemove', onMove, true);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setActive(null);
    };
    window.addEventListener('pointerdown', onDown as EventListener, true);
    window.addEventListener('keydown', onKeyDown, true);
    return () => {
      window.removeEventListener('pointerdown', onDown as EventListener, true);
      window.removeEventListener('keydown', onKeyDown, true);
    };
  }, [active]);

  if (!active) return null;

  return createPortal(
    <div
      ref={tipRef}
      role="tooltip"
      style={{
        position: 'fixed',
        top: pos?.top ?? -9999,
        left: pos?.left ?? -9999,
        zIndex: 99999,
        opacity: pos ? 1 : 0,
        transition: 'opacity 120ms ease',
        ...tooltipPillStyle(),
      }}
    >
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          width: 8,
          height: 8,
          background: 'var(--color-ink-900)',
          // The arrow tracks whichever edge faces the trigger.
          ...(pos?.placement === 'left' || pos?.placement === 'right'
            ? {
                top: '50%',
                [pos.placement === 'left' ? 'right' : 'left']: -4,
                transform: 'translateY(-50%) rotate(45deg)',
              }
            : {
                left: '50%',
                [pos?.placement === 'bottom' ? 'top' : 'bottom']: -4,
                transform: 'translateX(-50%) rotate(45deg)',
              }),
        }}
      />
      {active.text}
    </div>,
    document.body,
  );
}
